import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MonitorSmartphone, ArrowRight } from "lucide-react";
import { useLang, useSeo } from "../i18n/LangContext";
import { Reveal, Eyebrow, LineReveal } from "../components/site/Reveal";
import { API, CONTACT_EMAIL } from "../config";

const Field = ({ label, name, value, onChange, type = "text", required = true }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.24em] text-[#4A525A]">{label}{required && " *"}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-${name}-input`}
      className="mt-2 w-full border-b border-[#121518]/30 bg-transparent py-3 text-base outline-none transition-colors duration-300 focus:border-[#005F60]"
    />
  </label>
);

const Pills = ({ label, options, value, onPick, name, required }) => (
  <fieldset>
    <legend className="text-[11px] uppercase tracking-[0.24em] text-[#4A525A]">{label}{required && " *"}</legend>
    <div className="mt-3 flex flex-wrap gap-2">
      {Object.entries(options).map(([k, v]) => (
        <button
          type="button"
          key={k}
          onClick={() => onPick(k)}
          aria-pressed={value === k}
          data-testid={`contact-${name}-${k}`}
          className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${value === k ? "border-[#005F60] bg-[#005F60] text-[#FAF8F5]" : "border-[#121518]/25 hover:border-[#121518]"}`}
        >
          {v}
        </button>
      ))}
    </div>
  </fieldset>
);

const ContactForm = ({ f }) => {
  const [params] = useSearchParams();
  const initial = params.get("interesse");
  const [form, setForm] = useState({
    name: "", institution: "", role: "", email: "", message: "",
    interest: f.interests[initial] ? initial : "", format: "",
  });
  const [sending, setSending] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.institution || !form.role || !form.email || !form.interest) {
      toast.error(f.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, format: form.format || null });
      toast.success(f.ok);
      setForm({ name: "", institution: "", role: "", email: "", message: "", interest: "", format: "" });
    } catch {
      toast.error(f.err);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8 bg-white p-6 sm:p-10" data-testid="contact-form" noValidate>
      <h2 className="f-serif text-3xl">{f.title}</h2>
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label={f.name} name="name" value={form.name} onChange={set} />
        <Field label={f.institution} name="institution" value={form.institution} onChange={set} />
        <Field label={f.role} name="role" value={form.role} onChange={set} />
        <Field label={f.email} name="email" type="email" value={form.email} onChange={set} />
      </div>
      <Pills label={f.interest} name="interest" options={f.interests} value={form.interest} onPick={(v) => setForm({ ...form, interest: v })} required />
      <Pills label={f.format} name="format" options={f.formats} value={form.format} onPick={(v) => setForm({ ...form, format: v })} />
      <label className="block">
        <span className="text-[11px] uppercase tracking-[0.24em] text-[#4A525A]">{f.message}</span>
        <textarea name="message" value={form.message} onChange={set} rows={3} data-testid="contact-message-input" className="mt-2 w-full resize-none border-b border-[#121518]/30 bg-transparent py-3 outline-none focus:border-[#005F60]" />
      </label>
      <button type="submit" disabled={sending} className="btn-dark w-full justify-center sm:w-auto" data-testid="contact-submit-btn">
        {sending ? f.sending : f.submit} <ArrowRight size={16} />
      </button>
    </form>
  );
};

export default function Contact() {
  const { t } = useLang();
  useSeo("contact");
  const c = t.contactPage;
  return (
    <section className="mx-auto max-w-[1400px] px-6 pb-28 pt-36 sm:px-10 lg:pt-48" data-testid="contact-page">
      <Reveal><Eyebrow>{c.eyebrow}</Eyebrow></Reveal>
      <LineReveal lines={c.lines} className="f-serif mt-8 text-[clamp(3rem,9vw,8rem)] leading-[0.95] tracking-[-0.03em]" lineClassName="italic" />
      <div className="mt-14 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-lg leading-relaxed text-[#2E353B]">{c.intro}</p>
            <div className="mt-8 flex flex-col gap-3 text-sm">
              <span className="inline-flex items-center gap-3" data-testid="contact-modes"><MonitorSmartphone size={18} className="text-[#005F60]" /> {c.modes}</span>
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-3" data-testid="contact-email-link"><Mail size={18} className="text-[#005F60]" /> <span className="link-u">{CONTACT_EMAIL}</span></a>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-1">
            {c.offices.map((o, i) => (
              <Reveal key={o.city} delay={i * 0.1} data-testid={`office-${i}`}>
                <div className="relative flex aspect-[16/9] items-end overflow-hidden bg-[#EAE4D9] p-4">
                  <span className={`absolute right-0 top-0 h-full w-1/3 ${i ? "bg-[#FFCC00]" : "bg-[#005F60]"} [clip-path:polygon(40%_0,100%_0,100%_100%,0_100%)]`} aria-hidden="true" />
                  <span className="relative text-xs text-[#4A525A]">{o.photo}</span>
                </div>
                <p className="f-serif mt-5 text-2xl">{o.city}</p>
                <address className="mt-2 text-sm not-italic leading-relaxed text-[#2E353B]">
                  {o.addr.map((a) => <span key={a} className="block">{a}</span>)}
                </address>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <ContactForm f={c.form} />
        </Reveal>
      </div>
    </section>
  );
}
